import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Webhook as WebhookIcon, X, Globe, RefreshCw } from 'lucide-react';
import { api } from '@shared/services/api';
import { usePermissions } from '@shared/hooks/usePermissions';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneIconBgClasses } from '@shared/utils/statusTone';

interface Webhook {
  id: string;
  url: string;
  events: string[];
  secret: string;
  is_enabled: boolean;
  last_triggered_at?: string;
  created_at: string;
}

const webhookEventLabelKeys: Record<string, string> = {
  'backup.completed': 'webhooks.events.backupCompleted',
  'backup.failed': 'webhooks.events.backupFailed',
  'restore.started': 'webhooks.events.restoreStarted',
  'restore.completed': 'webhooks.events.restoreCompleted',
};

export default function Webhooks() {
  const { t, i18n } = useTranslation('common');
  const { canCreate, canEdit, canDelete } = usePermissions();
  const getEventLabel = (event: string) => {
    const labelKey = webhookEventLabelKeys[event];
    return labelKey ? t(labelKey) : event;
  };
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    url: '',
    events: [] as string[],
    secret: '',
    is_enabled: true,
  });
  const [creating, setCreating] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [createError, setCreateError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showCreateModal) return;
    setCreateError('');

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.querySelector<HTMLInputElement>('#webhook-target-url')?.focus();

    return () => previouslyFocused?.focus();
  }, [showCreateModal]);

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (creating) return;
      setShowCreateModal(false);
      return;
    }

    if (event.key !== 'Tab') return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusable = Array.from(
      dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!dialog.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get('/webhooks');
      setWebhooks(res.data.webhooks || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    setCreateError('');
    try {
      await api.post('/webhooks', newWebhook);
      setShowCreateModal(false);
      setNewWebhook({ url: '', events: [], secret: '', is_enabled: true });
      fetchWebhooks();
    } catch (_err) {
      setCreateError(t('webhooks.createFailure'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    if (actionPending) return;
    if (!window.confirm(t('webhooks.deleteConfirm'))) return;
    setActionPending(true);
    setActionMessage('');
    try {
      await api.delete(`/webhooks/${encodeURIComponent(id)}`);
      await fetchWebhooks();
    } catch {
      setActionMessage(t('webhooks.deleteFailure'));
    } finally {
      setActionPending(false);
    }
  };

  const handleToggle = async (webhook: Webhook) => {
    if (actionPending) return;
    setActionPending(true);
    setActionMessage('');
    try {
      await api.put(`/webhooks/${encodeURIComponent(webhook.id)}`, {
        is_enabled: !webhook.is_enabled,
      });
      await fetchWebhooks();
    } catch {
      setActionMessage(t('webhooks.updateFailure'));
    } finally {
      setActionPending(false);
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              Webhooks
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('webhooks.subtitle')}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button variant="outline" onClick={() => void fetchWebhooks()} className="min-h-11">
              <RefreshCw className="mr-2 h-4 w-4" /> {t('actions.refresh')}
            </Button>
            {canCreate && (
              <Button
                onClick={() => setShowCreateModal(true)}
                size="lg"
                className="min-h-11 whitespace-nowrap shadow-brand-200"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> {t('webhooks.register')}
              </Button>
            )}
          </div>
        </div>

        {actionMessage && (
          <p
            role="alert"
            className="rounded-lg border border-state-danger-light/30 bg-state-danger-bg-light px-4 py-3 text-sm text-state-danger-light dark:border-state-danger-dark/30 dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
          >
            {actionMessage}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4">
          {loading && webhooks.length === 0 ? (
            <div className="py-20 flex justify-center">
              <div className="animate-spin h-12 w-12 border-b-2 border-brand-600 rounded-full"></div>
            </div>
          ) : loadError ? (
            <div role="alert">
              <Card className="p-6 text-center">
                <p className="text-sm text-state-danger-light dark:text-state-danger-dark">
                  {t('webhooks.loadFailure')}
                </p>
                <Button className="mt-4" onClick={() => void fetchWebhooks()}>
                  <RefreshCw size={16} className="mr-2" />
                  {t('actions.retry')}
                </Button>
              </Card>
            </div>
          ) : webhooks.length === 0 ? (
            <Card className="py-24 text-center border-2 border-dashed">
              <WebhookIcon
                size={48}
                className="mx-auto text-text-subtle-light dark:text-text-subtle-dark mb-4"
              />
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                {t('webhooks.emptyTitle')}
              </h3>
              <p className="text-text-muted-light dark:text-text-muted-dark mt-2">
                {t('webhooks.emptyDescription')}
              </p>
            </Card>
          ) : (
            webhooks.map((webhook) => (
              <Card key={webhook.id} className="p-6 transition-all hover:border-brand-200 group">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div
                      className={`p-3 rounded-2xl ${
                        webhook.is_enabled
                          ? toneIconBgClasses[getStatusTone('ACTIVE')]
                          : toneIconBgClasses[getStatusTone('INACTIVE')]
                      }`}
                    >
                      <Globe size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <h3 className="min-w-0 max-w-full break-all font-bold text-slate-900 dark:text-white">
                          {webhook.url}
                        </h3>
                        <Badge
                          variant={
                            toneToBadgeVariant[
                              getStatusTone(webhook.is_enabled ? 'ACTIVE' : 'INACTIVE')
                            ]
                          }
                        >
                          {webhook.is_enabled ? t('webhooks.active') : t('webhooks.paused')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {webhook.events.map((ev) => (
                          <span
                            key={ev}
                            className="text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark bg-surface-2-light dark:bg-surface-2-dark px-2 py-0.5 rounded"
                          >
                            {getEventLabel(ev)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggle(webhook)}
                        disabled={actionPending}
                        title={webhook.is_enabled ? t('webhooks.pause') : t('webhooks.resume')}
                        aria-label={`${webhook.is_enabled ? t('webhooks.pause') : t('webhooks.resume')}: ${webhook.url}`}
                      >
                        <RefreshCw
                          size={16}
                          className="text-text-subtle-light dark:text-text-subtle-dark"
                        />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        disabled={actionPending}
                        title={t('webhooks.delete')}
                        aria-label={`${t('webhooks.delete')}: ${webhook.url}`}
                        className="text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark"
                      >
                        <Trash2 size={18} />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-4 border-t border-surface-border-light pt-4 dark:border-surface-border-dark sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-2 text-xs text-text-subtle-light dark:text-text-subtle-dark sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                    <span>
                      {t('webhooks.secret')}:{' '}
                      <span className="font-mono text-slate-600 dark:text-slate-300 bg-surface-2-light dark:bg-surface-2-dark px-1 rounded">
                        {webhook.secret}
                      </span>
                    </span>
                    <span>
                      {t('webhooks.lastDelivery')}:{' '}
                      {webhook.last_triggered_at
                        ? new Intl.DateTimeFormat(i18n.language, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }).format(new Date(webhook.last_triggered_at))
                        : t('webhooks.never')}
                    </span>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {showCreateModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="webhook-dialog-title"
          ref={dialogRef}
          onKeyDown={handleDialogKeyDown}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm animate-in fade-in"
        >
          <Card className="max-h-[calc(100dvh-2rem)] w-full max-w-xl overflow-y-auto p-5 shadow-elevated sm:p-8">
            <div className="flex justify-between items-center mb-8">
              <h2
                id="webhook-dialog-title"
                className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight"
              >
                {t('webhooks.dialogTitle')}
              </h2>
              <button
                type="button"
                aria-label={t('actions.close')}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                <X className="text-text-subtle-light dark:text-text-subtle-dark" />
              </button>
            </div>
            <form onSubmit={handleCreateWebhook} className="space-y-6">
              {createError && (
                <p
                  role="alert"
                  className="rounded-lg border border-state-danger-light/30 bg-state-danger-bg-light p-4 text-sm text-state-danger-light dark:border-state-danger-dark/30 dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                >
                  {createError}
                </p>
              )}
              <Input
                id="webhook-target-url"
                disabled={creating}
                label={t('webhooks.targetUrl')}
                type="url"
                placeholder="https://api.myapp.com/events"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                required
              />

              <fieldset className="space-y-3" disabled={creating}>
                <legend className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {t('webhooks.eventSubscriptions')}
                </legend>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'backup.completed',
                    'backup.failed',
                    'restore.started',
                    'restore.completed',
                  ].map((ev) => (
                    <label
                      key={ev}
                      className="flex min-h-11 items-center gap-3 rounded-xl bg-surface-2-light p-3 transition-colors hover:bg-surface-border-light dark:bg-surface-2-dark dark:hover:bg-surface-border-dark"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border-strong-light text-brand-600 dark:border-border-strong-dark dark:text-brand-400"
                        checked={newWebhook.events.includes(ev)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...newWebhook.events, ev]
                            : newWebhook.events.filter((x) => x !== ev);
                          setNewWebhook({ ...newWebhook, events: next });
                        }}
                      />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {getEventLabel(ev)}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <Input
                id="webhook-signing-secret"
                disabled={creating}
                label={t('webhooks.signingSecret')}
                placeholder={t('webhooks.secretPlaceholder')}
                value={newWebhook.secret}
                onChange={(e) => setNewWebhook({ ...newWebhook, secret: e.target.value })}
              />

              <div className="flex gap-3 pt-4">
                <Button
                  variant="secondary"
                  className="flex-1"
                  type="button"
                  disabled={creating}
                  onClick={() => setShowCreateModal(false)}
                >
                  {t('actions.cancel')}
                </Button>
                <Button className="flex-1" type="submit" isLoading={creating}>
                  {t('webhooks.createAction')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}
